import { useRef, useState, useCallback, useEffect } from 'react'
import { Box, Grid, Button } from '@mui/material'
import FormCreator from '@/shared/FormCreator'
import Table from '@/shared/Table'
import { useTranslation } from 'react-i18next'
import { connect, useSelector } from 'react-redux'
import usePermissions from '@/hooks/usePermissions'
import generateUsername from '../../components/Watermark/GenerateUsername'
import fieldToSortStr from '@/libs/utils/fieldToSortStr'
import get from 'lodash/get'
import Watermark from '../../components/Watermark/Watermark'
import debounce from '@mui/material/utils/debounce'
import unionBy from 'lodash/unionBy'
import moment from 'moment'
import { CSVLink } from 'react-csv'
import Download from '@mui/icons-material/Download'


const mapStateToProps = ({ auditlog, loading }) => ({
    auditlogList: auditlog.auditlogList,
    isLoading: loading['auditlog/getAuditlogData'],
})

const fieldMap = {
    account: 'ACCOUNT',
    companyName: 'COMP_NAME',
    entry: 'ENTRY',
    description: 'DESCRIPTION',
    createDate: 'CREATEDATE',
    response: 'RESPONSE'
}

const OperationLog = ({ dispatch, auditlogList, isLoading }) => {

    const form = useRef()
    const csvRef = useRef()
    const { t } = useTranslation()
    const { data, paging = {} } = auditlogList || {}
    const [sort, setSort] = useState([{ field: 'createDate', sort: 'desc' }])
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)
    const companyPageRef = useRef(0)
    const [companyResults, setCompanyResults] = useState({})
    const { data: companyResultsData = [] } = companyResults
    const [filterDateRange, setFilterDateRange] = useState()
    const [csvData, setCsvData] = useState()

    const employeeId = useSelector(state => state.auth.account?.employeeId)

    const accountEmail = useSelector(state => state.auth.account?.accountEmail)

    const accountId = useSelector(state => state.auth.account?.accountId)

    const username = generateUsername(employeeId, accountEmail)

    const canDownload = usePermissions('auditlog_download')

    const pagination = {
        ...paging,
        page,
        pageSize,
        onPageChange: setPage,
        onPageSizeChange: setPageSize,
    }

    const cols = [
        {
            field: 'account',
            headerName: t('general.account_name'),
            width: 240,
        },
        { field: 'companyName', headerName: t('general.company_name'), width: 200 },
        { field: 'entry', headerName: t('general.operation_type'), width: 200 },
        { field: 'description', headerName: t('general.operation_content'), width: 300 },
        { field: 'createDate', headerName: t('general.operation_time'), width: 180 },
        {
            field: 'response',
            headerName: t('general.result'),
            minWidth: 40,
            valueFormatter: ({ value }) => {
                if (!value) return '-'

                const upper = String(value).toUpperCase()

                if (upper === 'ERROR') return t('status.FAIL')
                if (upper === 'SUCCESS') return t('status.SUC')

                return '-'
            },
        }
    ]

    const fetchData = useCallback(() => {

        const values = form.current?.getValues() || {}

        const hasRpaStart = values?.rpaStartDate
        const hasRpaEnd = values?.rpaEndDate

        const startDate = hasRpaStart
            ? moment(values.rpaStartDate).format('YYYY-MM-DD')
            : moment(filterDateRange?.startDate).format('YYYY-MM-DD')

        const endDate = hasRpaEnd
            ? moment(values.rpaEndDate).format('YYYY-MM-DD')
            : moment(filterDateRange?.endDate).format('YYYY-MM-DD')

        // 從 form 拿多欄位條件
        const payload = {
            startDate,
            endDate,
            pageIndex: page,
            pageSize,
            sort: fieldToSortStr(get(sort, '[0]'), fieldMap),
            companyId: values?.company?.value || null,
            accountName: values?.account || null,
        }

        dispatch({
            type: 'auditlog/getAuditlogData',
            payload,
        })
    }, [filterDateRange, sort, page, pageSize, dispatch])

    const getCompany = params => {
        const { companyPage = 0, keyword = '' } = params || {}

        companyPageRef.current = companyPage

        dispatch({
            type: 'account/getCompany',
            payload: {
                pageIndex: companyPageRef.current,
                pageSize: 10,
                keyword, //  關鍵字
            },
            successCallback: response => {
                const { data: responseData = [], paging: responsePaging = {} } = response || {}
                const { pageIndex } = responsePaging

                const newData = responseData.map(d => ({
                    value: d.companyId,
                    label: d.companyName,
                }))

                setCompanyResults(s => ({
                    data: pageIndex > 0 ? unionBy(s.data || [], newData, 'value') : newData,
                    paging: responsePaging,
                }))
            },
        })
    }

    const fetchCompanyData = useCallback(
        companyNameValue => {
            const keyword = companyNameValue || form.current?.getValues()?.companyName?.label
            const payload = {
                pageIndex: companyPageRef.current,
                pageSize: 10,
                keyword: keyword || '', // 空值也可以發送
            }

            dispatch({
                type: 'account/getCompany',
                payload,
                successCallback: response => {
                    const { data: responseData = [], paging: responsePaging = {} } = response || {}
                    const { pageIndex } = responsePaging

                    const newData = responseData.map(d => ({
                        value: d.companyId,
                        label: d.companyName,
                    }))

                    setCompanyResults(s => ({
                        data: pageIndex > 0
                            ? unionBy(s.data || [], newData, 'value')
                            : newData,
                        paging: responsePaging,
                    }))
                },
            })
        },
        [dispatch],
    )

    const setCompanyNameDebounce = debounce((_, value) => {
        companyPageRef.current = 0
        fetchCompanyData(value)
    }, 400)

    const downloadReport = () => {

        const values = form.current?.getValues() || {}

        const hasRpaStart = values?.rpaStartDate
        const hasRpaEnd = values?.rpaEndDate

        const startDate = hasRpaStart
            ? moment(values.rpaStartDate).format('YYYY-MM-DD')
            : moment(filterDateRange?.startDate).format('YYYY-MM-DD')

        const endDate = hasRpaEnd
            ? moment(values.rpaEndDate).format('YYYY-MM-DD')
            : moment(filterDateRange?.endDate).format('YYYY-MM-DD')

        // 從 form 拿多欄位條件
        const payload = {
            startDate,
            endDate,
            pageIndex: page,
            pageSize,
            sort: fieldToSortStr(get(sort, '[0]'), fieldMap),
            companyId: values?.company?.value || null,
            accountName: values?.account || null,
        }

        dispatch({
            type: 'auditlog/reportDownload',
            payload,
            successCallback: response => {
                setCsvData({ filename: `MAIP_auditlog_${moment(startDate).format('YYYYMM')}.csv`, data: response })
            },
        })
    }

    useEffect(() => {
        const startDate = moment()
            .subtract(6, 'day')
            .startOf('day')
            .toDate()

        const endDate = moment()
            .endOf('day')
            .toDate()

        form.current?.setValue?.('dateRangeFilter', {
            startDate,
            endDate,
        })

        setFilterDateRange({
            startDate,
            endDate,
        })
    }, [])

    useEffect(() => {
        if (csvData) {
            csvRef.current?.link?.click?.()
        }
    }, [csvData])

    useEffect(() => {
        if (!accountId) return
        fetchData()
        getCompany()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountId, fetchData, page, pageSize]);


    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: 520,
                    gap: 2,
                    border: '1px solid #2D2D6C',
                    borderRadius: 3,
                    overflow: 'hidden',
                    p: 2,
                    backgroundColor: '#fff',
                }}
            >
                {/* 搜尋區 */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        mb: 2,
                        alignItems: 'center',
                    }}
                >
                    <Grid item>
                        <FormCreator
                            ref={form}
                            formItems={[
                                {
                                    name: 'account',
                                    placeholder: t('general.insert_account_name'),
                                    defaultValue: '',
                                    inputConfig: {
                                        onSubmit: async () => {
                                            setPage(0)
                                            fetchData()
                                        }
                                    },
                                },
                                {
                                    type: 'autocomplete',
                                    name: 'company',
                                    defaultValue: null,
                                    inputConfig: {
                                        options: companyResultsData,
                                        isOptionEqualToValue: (option, value) => option?.value === value?.value,
                                        onChange: selectedOption => {
                                            form.current.setValue('company', selectedOption || null)
                                            form.current.setValue('companyName', selectedOption?.label || '')
                                            companyPageRef.current = 0
                                            fetchData()
                                        },
                                        onInputChange: (_, inputValue) => {
                                            setCompanyNameDebounce(_, inputValue) //  debounce 處理 keyword
                                        },
                                        placeholder: t('general.company_name'),
                                    },
                                },
                                {
                                    label: t('general.custom_date_range'),
                                    name: 'dateRangeFilter',
                                    type: 'dateRange',
                                    defaultValue: { startDate: new Date(), endDate: new Date() },
                                    inputConfig: {
                                        onChange: value => {
                                            setFilterDateRange(value)
                                        },
                                    },
                                },
                                ...(canDownload
                                    ? [
                                        {
                                            label: t('general.specified_date_range'),
                                            placeholder: t('general.start_date_placeholder'),
                                            name: 'rpaStartDate',
                                            defaultValue: '',
                                            inputConfig: {
                                                sx: {
                                                    width: 150,   // 調整寬度
                                                },
                                            },
                                        }]
                                    : []),
                                ...(canDownload
                                    ? [
                                        {
                                            placeholder: t('general.end_date_placeholder'),
                                            name: 'rpaEndDate',
                                            defaultValue: '',
                                            inputConfig: {
                                                sx: {
                                                    width: 200,   // 調整寬度
                                                },
                                                onSubmit: async () => {
                                                    setPage(0)
                                                    fetchData()
                                                }
                                            }
                                        }]
                                    : []),
                                ...(canDownload
                                    ? [
                                        {
                                            component: () => (
                                                <Button
                                                    variant="text"
                                                    color="secondary_80"
                                                    endIcon={<Download />}
                                                    onClick={() => {
                                                        downloadReport()
                                                    }}
                                                    sx={{
                                                        fontSize: '14px', // 放大字體
                                                    }}
                                                >
                                                    {t('general.download_audit_report')}
                                                </Button>
                                            ),
                                        },
                                    ]
                                    : []),
                            ]}
                            flexDirection="row"
                            submitComponent={() => null}
                            resetComponent={() => null}
                            gridItemProps={{ mr: 3 }}
                        />
                    </Grid>
                    <Box sx={{ flexGrow: 1 }} />
                </Box>

                {/* 表格區 */}
                <Table
                    minHeight={430}
                    columns={cols}
                    rows={data || []}
                    dataGridConfig={{
                        sortingMode: 'server',
                        sortingOrder: ['desc', 'asc'],
                        getRowId: row => row.id,
                        sortModel: sort,
                        onSortModelChange: newSort => setSort(newSort),
                        loading: isLoading,
                    }}
                    noRowsLabel={t('errors.no_data')}
                    pagination={pagination}
                />
                <Watermark username={username} />
            </Box>

            <CSVLink ref={csvRef} filename={csvData?.filename || ''} data={csvData?.data || []} />

        </>
    )
}

export default connect(mapStateToProps)(OperationLog)